/**
 * Enroller — State machine for `device.enroll` and `device.rotate-secret`.
 *
 * States per `2.需求澄清.md` §1.2:
 *   anonymous → pending → claimed → expired
 *
 *   - `anonymous` (initial): no device has ever enrolled. Server returns
 *     a fresh `publicId` + secret.
 *   - `pending`: enrollment HTTP call has been issued but the server
 *     hasn't confirmed yet. In-flight state — never persisted.
 *   - `claimed`: user has linked this device to their account (via
 *     `/api/v1/devices/claim`). Sticky binding locks future re-enrolls
 *     to the same `userId` (server-side matrix).
 *   - `expired`: server returned a device-expiry error. Forces a fresh
 *     enroll on next call.
 *
 * `--force` semantics: any non-anonymous state can be force-reset to
 * `anonymous` by wiping the local identity file. The next enroll will
 * be treated as a brand-new install by the server (no sticky binding).
 *
 * The Enroller is the **state machine**; the actual HTTP I/O is the
 * caller's responsibility (the `DeviceSyncClient` in Phase 5.4 wires
 * the server). This split keeps the Enroller unit-testable without
 * a live server.
 *
 * Refs:
 * - 4.功能规划.md §2.2 — `Enroller.ts anonymous → pending → claimed → expired`
 * - `2.需求澄清.md` §1.2 — binding-state machine
 */

import { randomBytes } from "node:crypto";

import type { DeviceBindingState, DeviceEnrollResult } from "@serviceme/devtools-protocol";
import type { IdentityStore } from "./IdentityStore";
import { deriveInstallationId } from "./InstallationId";
import type { PersistedDeviceIdentity } from "./types";

/** 32 bytes of HMAC secret material — matches the server's `device-registration.ts:73-80` generator. */
const SECRET_BYTES = 32;
/** Server returns `publicId` as 32-char hex (16 bytes). Match the wire length. */
const PUBLIC_ID_BYTES = 16;

type RandomBytesFn = (size: number) => Buffer;

const defaultRandomBytes: RandomBytesFn = (size) => {
	return randomBytes(size);
};

export interface EnrollerOptions {
	identityStore: IdentityStore;
	/** Injectable clock for deterministic tests. */
	now?: () => Date;
	/** Override the random source (tests). */
	randomBytes?: (size: number) => Buffer;
	/** Caller-supplied enroll HTTP function. Phase 5.4 wires the real one. */
	enrollRequest?: EnrollRequestFn;
}

export type EnrollRequestFn = (input: {
	installationId: string;
	machineId: string;
	platform: string;
	existing: PersistedDeviceIdentity | null;
	force: boolean;
	requireAuth: boolean;
}) => Promise<EnrollResponse>;

export interface EnrollResponse {
	publicId: string;
	deviceSecret: string;
	secretVersion: number;
	bindingState: DeviceBindingState;
	expiresAt?: string;
}

/** Sentinel error — re-enroll on a claimed device without auth. */
export class DeviceReenrollRequiresAuthError extends Error {
	constructor(
		message = "Re-enroll on a claimed device requires current device credentials or the bound user"
	) {
		super(message);
		this.name = "DeviceReenrollRequiresAuthError";
	}
}

/** Sentinel error — server returned a 410 / version-mismatch after rotation. */
export class DeviceSecretVersionMismatchError extends Error {
	constructor(message = "Device secret version mismatch — server has rotated past the local copy") {
		super(message);
		this.name = "DeviceSecretVersionMismatchError";
	}
}

export class Enroller {
	private readonly identity: IdentityStore;
	private readonly now: () => Date;
	private readonly random: (size: number) => Buffer;
	private readonly enrollRequest?: EnrollRequestFn;
	private inflight: Promise<DeviceEnrollResult> | null = null;

	constructor(opts: EnrollerOptions) {
		this.identity = opts.identityStore;
		this.now = opts.now ?? (() => new Date());
		this.random = opts.randomBytes ?? defaultRandomBytes;
		this.enrollRequest = opts.enrollRequest;
	}

	/**
	 * Read the current binding state without touching the disk.
	 * Returns `anonymous` when no identity is stored.
	 */
	async currentState(): Promise<DeviceBindingState> {
		const stored = await this.identity.read();
		return stored?.bindingState ?? "anonymous";
	}

	/**
	 * Drive the enrollment flow.
	 *
	 * @param force when true, drop the local identity and start fresh
	 *              (server treats this as a brand-new install).
	 * @param requireAuth when true, refuse to silently re-enroll an
	 *              existing claimed device — throw
	 *              `DeviceReenrollRequiresAuthError` instead.
	 */
	/**
	 * Resolve when any in-flight enrollment completes.  Returns immediately
	 * when no enrollment is in progress.  Allows callers (e.g. the extension's
	 * `buildDeviceAuthHeaders`) to wait for a concurrent `syncDeviceInfo()`
	 * enrollment before attempting to read the identity from the store.
	 */
	async waitForEnrollment(): Promise<void> {
		if (this.inflight) {
			await this.inflight;
		}
	}

	async enroll(opts: { force?: boolean; requireAuth?: boolean } = {}): Promise<DeviceEnrollResult> {
		// Concurrency guard — multiple in-flight calls share the same promise.
		if (this.inflight) {
			return this.inflight;
		}
		const promise = this.runEnroll(opts);
		this.inflight = promise;
		try {
			return await promise;
		} finally {
			if (this.inflight === promise) this.inflight = null;
		}
	}

	/** Test seam — surface the underlying identity store. */
	getIdentityStore(): IdentityStore {
		return this.identity;
	}

	/** True when an enrollment is currently in-flight. Used by callers (e.g. the extension's `buildDeviceAuthHeaders`) to skip triggering a competing enrollment. */
	isEnrolling(): boolean {
		return this.inflight !== null;
	}

	private async runEnroll(opts: {
		force?: boolean;
		requireAuth?: boolean;
	}): Promise<DeviceEnrollResult> {
		const { written } = await this.identity.mutate(async (current) => {
			const existing = opts.force ? null : current;

			if (!opts.force && current) {
				if (current.bindingState === "expired") {
					// Expired identities are forced to re-enroll as if they were new.
				} else if (
					opts.requireAuth &&
					(current.bindingState === "claimed" || current.bindingState === "pending")
				) {
					// Caller asserted the device must be claimed, but local state
					// shows it's still in flight. This is a CLI-only guard — the
					// server is the final arbiter.
					throw new DeviceReenrollRequiresAuthError();
				}
			}

			const installationId = current?.installationId ?? deriveInstallationId();
			const machineId = current?.machineId ?? "unknown";
			const platform = current?.platform ?? "unknown";

			let response: EnrollResponse;
			if (this.enrollRequest) {
				response = await this.enrollRequest({
					installationId,
					machineId,
					platform,
					existing,
					force: Boolean(opts.force),
					requireAuth: Boolean(opts.requireAuth),
				});
			} else {
				// Test path / no live HTTP — synthesize a fresh identity. This
				// branch is what unit tests exercise; production wires
				// `enrollRequest` in Phase 5.4.
				response = synthesizeEnrollResponse(this.random, existing);
			}

			const next: PersistedDeviceIdentity = {
				version: current?.version ?? 1,
				installationId,
				machineId,
				platform,
				hostname: current?.hostname,
				publicId: response.publicId,
				secretVersion: response.secretVersion,
				bindingState: response.bindingState,
				deviceSecret: response.deviceSecret,
				previousDeviceSecret: existing?.deviceSecret,
				previousSecretExpiresAt:
					opts.force || response.secretVersion === (existing?.secretVersion ?? 0) + 1
						? undefined
						: existing?.previousSecretExpiresAt,
				lastEnrollAt: this.now().toISOString(),
				lastSyncAt: existing?.lastSyncAt,
				lastSyncError: undefined,
			};
			return { next };
		});

		return {
			publicId: written.publicId,
			bindingState: written.bindingState,
			expiresAt: deriveExpiresAt(written, this.now),
		};
	}

	/**
	 * Rotate the HMAC secret. Keeps the previous secret for the grace
	 * window (default 7 days per `2.需求澄清.md` §1.2) — the
	 * `previousSecretExpiresAt` is stamped on the persisted identity.
	 */
	async rotateSecret(
		opts: { gracePeriodDays?: number } = {}
	): Promise<{ publicId: string; secretVersion: number; gracePeriodDays: number }> {
		const gracePeriodDays = opts.gracePeriodDays ?? 7;
		const now = this.now();
		const newSecret = this.random(SECRET_BYTES).toString("hex");

		const { written } = await this.identity.mutate(async (current) => {
			if (!current) {
				throw new Error("Cannot rotate-secret without a prior enrollment");
			}
			const graceExpiresAt = new Date(now.getTime() + gracePeriodDays * 24 * 60 * 60 * 1000);
			const next: PersistedDeviceIdentity = {
				...current,
				deviceSecret: newSecret,
				previousDeviceSecret: current.deviceSecret,
				previousSecretExpiresAt: graceExpiresAt.toISOString(),
				secretVersion: current.secretVersion + 1,
				lastEnrollAt: now.toISOString(),
			};
			return { next };
		});

		return {
			publicId: written.publicId,
			secretVersion: written.secretVersion,
			gracePeriodDays,
		};
	}

	/**
	 * Mark the device as `expired`. Used when the server returns a
	 * device-expiry response; the next `enroll()` call forces a fresh
	 * round-trip.
	 */
	async markExpired(): Promise<void> {
		await this.identity.mutate(async (current) => {
			if (!current) {
				// Nothing to expire.
				return { next: current ?? (await emptyIdentity(this.random)), result: undefined };
			}
			const next: PersistedDeviceIdentity = { ...current, bindingState: "expired" };
			return { next };
		});
	}

	/**
	 * Mark the device as `claimed`. Called by the bridge after a
	 * successful `device.claim` server response.
	 */
	async markClaimed(): Promise<void> {
		await this.identity.mutate(async (current) => {
			if (!current) {
				throw new Error("Cannot mark-claimed without a prior enrollment");
			}
			const next: PersistedDeviceIdentity = {
				...current,
				bindingState: "claimed",
				lastSyncAt: this.now().toISOString(),
				lastSyncError: undefined,
			};
			return { next };
		});
	}
}

function deriveExpiresAt(_identity: PersistedDeviceIdentity, _now: () => Date): string | undefined {
	// No explicit expiry on the server today (per phase-5-device-header-spec.md
	// §9 #1: 7-day grace is rotation-only). The shape is here for forward
	// compatibility — when the server adds a per-device expiry, this
	// pulls the value from the response without changing the call site.
	return undefined;
}

function synthesizeEnrollResponse(
	random: RandomBytesFn,
	existing: PersistedDeviceIdentity | null
): EnrollResponse {
	const publicId = existing?.publicId ?? random(PUBLIC_ID_BYTES).toString("hex");
	const secretVersion = (existing?.secretVersion ?? 0) + 1;
	return {
		publicId,
		deviceSecret: random(SECRET_BYTES).toString("hex"),
		secretVersion,
		bindingState: existing?.bindingState === "claimed" ? "claimed" : "anonymous",
	};
}

async function emptyIdentity(random: RandomBytesFn): Promise<PersistedDeviceIdentity> {
	return {
		version: 1,
		installationId: deriveInstallationId(),
		machineId: "unknown",
		platform: "unknown",
		publicId: random(PUBLIC_ID_BYTES).toString("hex"),
		secretVersion: 1,
		bindingState: "anonymous",
		deviceSecret: random(SECRET_BYTES).toString("hex"),
		lastEnrollAt: new Date().toISOString(),
	};
}
